import React, {useMemo, useRef} from 'react';
import {Canvas, useFrame} from '@react-three/fiber';
import * as THREE from 'three';

const BALL_RADIUS = 0.55;
const PENTAGON_COUNT = 10;

// distribuie N puncte uniform pe suprafata unei sfere unitare (spirala Fibonacci)
// — evita distorsiunea unei texturi 2D infasurate pe sfera (efect de "pinch" la poli)
const fibonacciSpherePoints = (count) => {
    const points = [];
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < count; i++) {
        const y = 1 - (i / (count - 1)) * 2;
        const r = Math.sqrt(Math.max(0, 1 - y * y));
        const theta = goldenAngle * i;
        points.push(new THREE.Vector3(Math.cos(theta) * r, y, Math.sin(theta) * r));
    }
    return points;
};

const UP = new THREE.Vector3(0, 0, 1);

const Ball = ({isWin}) => {
    const groupRef = useRef();
    const pentagonDirs = useMemo(() => fibonacciSpherePoints(PENTAGON_COUNT), []);

    useFrame((_, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * 1.4;
            groupRef.current.rotation.x += delta * 0.5;
        }
    });

    return (
        <group ref={groupRef} position={[0, 0, isWin ? -0.15 : 0.15]}>
            <mesh>
                <sphereGeometry args={[BALL_RADIUS, 32, 32]}/>
                <meshStandardMaterial color="#f4f4f5" roughness={0.55}/>
            </mesh>
            {pentagonDirs.map((dir, i) => (
                <mesh
                    key={i}
                    position={dir.clone().multiplyScalar(BALL_RADIUS * 1.01)}
                    quaternion={new THREE.Quaternion().setFromUnitVectors(UP, dir)}
                >
                    <circleGeometry args={[BALL_RADIUS * 0.32, 5]}/>
                    <meshStandardMaterial color="#111827" roughness={0.6} side={THREE.DoubleSide}/>
                </mesh>
            ))}
        </group>
    );
};

// plasa de poarta: grid de linii care se "umfla" spre spate cand mingea a intrat (win)
const buildNetPositions = (size, divisions, bulge) => {
    const half = size / 2;
    const step = size / divisions;
    const bulgeAt = (x, y) => {
        const d = Math.sqrt(x * x + y * y) / (half * Math.SQRT2);
        return bulge * Math.max(0, 1 - d);
    };

    const positions = [];
    for (let i = 0; i <= divisions; i++) {
        const t = -half + step * i;
        for (let j = 0; j < divisions; j++) {
            const y0 = -half + step * j;
            const y1 = -half + step * (j + 1);
            positions.push(t, y0, bulgeAt(t, y0), t, y1, bulgeAt(t, y1));
        }
        for (let j = 0; j < divisions; j++) {
            const x0 = -half + step * j;
            const x1 = -half + step * (j + 1);
            positions.push(x0, t, bulgeAt(x0, t), x1, t, bulgeAt(x1, t));
        }
    }
    return new Float32Array(positions);
};

const Net = ({isWin}) => {
    const positions = useMemo(
        () => buildNetPositions(1.8, 7, isWin ? -0.4 : -0.05),
        [isWin]
    );

    return (
        <lineSegments position={[0, 0, -0.5]}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    array={positions}
                    count={positions.length / 3}
                    itemSize={3}
                />
            </bufferGeometry>
            <lineBasicMaterial color="#ffffff" transparent opacity={0.55}/>
        </lineSegments>
    );
};

const SoccerBallNet = ({isWin}) => (
    <Canvas
        camera={{position: [0, 0, 2.3], fov: 42}}
        gl={{alpha: true, antialias: true}}
        style={{width: '100%', height: '100%'}}
    >
        <ambientLight intensity={0.9}/>
        <directionalLight position={[1.5, 2, 2]} intensity={1}/>
        <Net isWin={isWin}/>
        <Ball isWin={isWin}/>
    </Canvas>
);

export default SoccerBallNet;
